import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DEMO_MODE } from "../lib/config";
import { api } from "../lib/api";
import { useToastStore } from "../store/toast";
import { DEMO_PATIENT, DEMO_SESSION } from "../lib/demoData";
import type { Patient } from "../types";

const TREATMENT_TYPES = [
  "Full arch — upper",
  "Full arch — lower",
  "Full mouth — both arches",
  "Single implant",
  "Multiple implants",
  "Other",
];

export function SessionNewPage() {
  const [params] = useSearchParams();
  const initialPatientId = params.get("patientId") ?? "";
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);

  const [patientId, setPatientId] = useState(initialPatientId);
  const [treatmentType, setTreatmentType] = useState(TREATMENT_TYPES[0]!);
  const [treatmentNotes, setTreatmentNotes] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: patients } = useQuery({
    queryKey: ["patients-min"],
    queryFn: async (): Promise<Patient[]> => {
      if (DEMO_MODE) return [DEMO_PATIENT];
      return await api.get<Patient[]>("/patients");
    },
  });

  const submit = async () => {
    if (!patientId || !treatmentType || !requestedAmount) {
      showToast("Pick a patient, treatment, and amount", "warn");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        patientId,
        treatmentType,
        treatmentNotes: treatmentNotes.trim(),
        requestedAmount: Number(requestedAmount),
      };
      if (!DEMO_MODE) {
        const created = await api.post<{ id: string }>("/sessions", payload);
        navigate(`/sessions/${created.id}`);
      } else {
        showToast("Started session (demo)", "ok");
        navigate(`/sessions/${DEMO_SESSION.id}`);
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to create session", "warn");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">New session</h1>
          <p className="page__sub">Pick a patient and the treatment you're financing.</p>
        </div>
      </div>

      <div className="form">
        <label className="form__row">
          <span className="form__label">Patient</span>
          <select
            className="form__select"
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
          >
            <option value="">Select a patient…</option>
            {patients?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.firstName} {p.lastName} (DOB {p.dob})
              </option>
            ))}
          </select>
          <span className="form__hint">
            Don't see them? <Link to="/patients/new">Add a patient</Link>.
          </span>
        </label>

        <label className="form__row">
          <span className="form__label">Treatment type</span>
          <select
            className="form__select"
            value={treatmentType}
            onChange={(e) => setTreatmentType(e.target.value)}
          >
            {TREATMENT_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>

        <label className="form__row">
          <span className="form__label">Treatment notes</span>
          <textarea
            className="form__textarea"
            value={treatmentNotes}
            placeholder="e.g. All-on-4, upper arch. Includes extractions, immediate load, final zirconia at month 4."
            onChange={(e) => setTreatmentNotes(e.target.value)}
          />
        </label>

        <label className="form__row">
          <span className="form__label">Requested amount</span>
          <input
            className="form__input"
            inputMode="numeric"
            value={requestedAmount}
            placeholder="38500"
            onChange={(e) => setRequestedAmount(e.target.value.replace(/[^0-9]/g, ""))}
          />
        </label>

        <div className="form__actions">
          <button className="btn btn--ghost" onClick={() => navigate(-1)} disabled={busy}>Cancel</button>
          <button className="btn btn--solid" onClick={submit} disabled={busy}>
            {busy ? "Starting…" : "Start session"}
          </button>
        </div>
      </div>
    </div>
  );
}
